import { EventApi } from '@fullcalendar/common';
import { Listbox, Transition } from '@headlessui/react';
import { DateTime, Duration } from 'luxon';
import React, { Fragment, useState, FC, useContext } from 'react';
import { BsChevronLeft } from 'react-icons/bs';
import {
  ExternalContact,
  FlexCalAdditionalField,
  FlexCalSummary,
  InvitationAdditionalFieldResponse,
  Place,
  Reservation,
  SelectedLocation,
  TimeZone,
  VirtualLocation,
} from '../../utils/types';
import { HiSelector, HiCheck } from 'react-icons/hi';
import {
  Field,
  FieldArray,
  Form,
  Formik,
  FormikErrors,
  FormikTouched,
} from 'formik';
import './CalendarHeader.module.css';
import { AdditionalAttendee } from '../../utils/types';
import { AiOutlinePlus, AiOutlinePlusCircle } from 'react-icons/ai';
import { IoIosRemoveCircleOutline } from 'react-icons/io';
import { IoWarningOutline } from 'react-icons/io5';
import { useRouter } from 'next/router';
import {
  AdditionalFieldStatus,
  AdditionalFieldType,
  LoadingOrigin,
  PhysicalVirtualLocationEnum,
} from '../../utils/enums';
import { ClipLoader } from 'react-spinners';
import { LoadingContext } from '../../utils/contexts/loadingContext';
import formatDate from '../../utils/helpers/formatDate';
import adjustForTimezoneMeetingForm from '../../utils/helpers/adjustForMeetingFormTimeZone';
import * as Yup from 'yup';
import {
  Checkbox,
  Option,
  Radio,
  Select,
  Tooltip,
} from '@material-tailwind/react';
import overrideYupTypeError from '../../utils/helpers/overrideYupTypeError';
import Autocomplete from 'react-autocomplete';
import { UserInfo } from '../../utils/interfaces';
import * as Sentry from '@sentry/nextjs';

interface Values {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  subject?: string;
  additionalAttendees?: AdditionalAttendee[];
  additionalFieldResponses?: InvitationAdditionalFieldResponse[];
}

type Props = {
  clickedEvent: EventApi | undefined;
  setIsEventClicked: React.Dispatch<React.SetStateAction<boolean>>;
  flexCal: FlexCalSummary;
  filteredLocation: SelectedLocation;
  timeZone: TimeZone;
  apiUri: string;
  additionalFields: FlexCalAdditionalField[] | undefined;
  contacts: ExternalContact[] | undefined;
  userInfo: UserInfo;
};

enum AttendeeSelectType {
  Email = 1,
  Name = 2,
}
const MeetingForm: FC<Props> = ({
  clickedEvent,
  setIsEventClicked,
  flexCal,
  filteredLocation,
  timeZone,
  apiUri,
  additionalFields,
  contacts,
  userInfo,
}) => {
  //If there is no clickedEvent or data is not passed through, escpape this component
  if (
    clickedEvent === undefined ||
    clickedEvent?.start === null ||
    clickedEvent?.end === null
  ) {
    return <></>;
  }
  const { loading, setLoading } = useContext(LoadingContext);
  const router = useRouter();
  const [addAttendee, setAddAttendee] = useState<boolean>(false);

  //Represents the length of the event
  const eventDuration: Duration = DateTime.fromJSDate(clickedEvent?.end).diff(
    DateTime.fromJSDate(clickedEvent?.start)
  );
  //Physcial locations for this event
  const eventPhysicalLocations: Place[] =
    clickedEvent?.extendedProps.physicalLocations;
  //Virtual locations for this event
  const eventVirtualLocations: VirtualLocation[] =
    clickedEvent?.extendedProps.virtualLocations;
  //Combined list of names for virtual and physical locations for this event
  const eventLocations: string[] = eventPhysicalLocations
    .map((epl) => epl.name)
    .concat(eventVirtualLocations.map((evl) => evl.label));

  //The currently selected location
  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation>(filteredLocation);

  const getFirstName = () => {
    if (router.query.firstName) {
      return router.query.firstName as string;
    }
    return '';
  };

  const getLastName = () => {
    if (router.query.lastName) {
      return router.query.lastName as string;
    }
    return '';
  };

  const getEmail = () => {
    if (router.query.email) {
      return router.query.email as string;
    }
    return '';
  };

  const getPhone = () => {
    if (router.query.phone) {
      return router.query.phone as string;
    }
    return '';
  };

  const getSubject = () => {
    if (router.query.subject) {
      return router.query.subject as string;
    }
    return '';
  };

  //Checks URL for additional attendess and populates array in initial values
  const getAdditionalAttendees = () => {
    if (router.query.attendeeEmails) {
      const attendeeEmails = (router.query.attendeeEmails as string).split(',');
      const attendeeNames = (router.query.attendeeNames as string).split(',');

      const additionalAttendees: AdditionalAttendee[] = [];

      attendeeEmails.forEach((email, index) => {
        additionalAttendees.push({
          name: attendeeNames[index].replace(/([A-Z])/g, ' $1').trim(),
          email: email,
        });
      });
      return additionalAttendees;
    }
    return [];
  };

  const setBlankAdditionalFieldResponses = ():
    | InvitationAdditionalFieldResponse[]
    | undefined => {
    if (!additionalFields) return;

    return additionalFields?.map((af) => {
      return {
        fieldName: af.fieldName,
        response: '',
        additionalFieldType: af.additionalFieldType,
      };
    });
  };

  //Initial form values, currently all set to empty
  const [formValues] = useState<Values>({
    firstName: getFirstName(),
    lastName: getLastName(),
    email: getEmail(),
    phone: getPhone(),
    subject: getSubject(),
    additionalAttendees: getAdditionalAttendees(),
    additionalFieldResponses: setBlankAdditionalFieldResponses(),
  });

  const handleLocationChange = (locationName: string) => {
    if (selectedLocation.name !== locationName) {
      const physicalLocation = eventPhysicalLocations.find(
        (location) => location.name === locationName
      );
      const virtualLocation = eventVirtualLocations.find(
        (location) => location.label === locationName
      );
      if (physicalLocation !== undefined) {
        setSelectedLocation({
          id: physicalLocation.placeId,
          name: physicalLocation.name,
          locationType: PhysicalVirtualLocationEnum.physical,
        });
      }
      if (virtualLocation !== undefined) {
        setSelectedLocation({
          id: virtualLocation.virtualLocationId,
          name: virtualLocation.label,
          locationType: PhysicalVirtualLocationEnum.virtual,
        });
      }
    }
  };

  const getParticipantUserId = (): string | undefined => {
    if (userInfo?.sub) {
      return userInfo?.sub;
    } else {
      return undefined;
    }
  };

  const handleSubmit = async (
    values: Values,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    //Reservation object to send to BE after submission
    const reservation: Reservation = {
      reservationId: 0,
      startTime: clickedEvent?.start
        ? adjustForTimezoneMeetingForm(
            clickedEvent,
            clickedEvent?.start,
            timeZone.tzdbId
          ).toISOString()
        : new Date().toISOString(),
      endTime: clickedEvent?.end
        ? adjustForTimezoneMeetingForm(
            clickedEvent,
            clickedEvent?.end,
            timeZone.tzdbId
          ).toISOString()
        : new Date().toISOString(),
      participantFirstName: values.firstName!,
      participantLastName: values.lastName!,
      participantEmail: values.email!,
      participantPhoneNumber: values.phone!,
      participantAccetpsSMS: true,
      participantUserId: getParticipantUserId(),
      subject: values.subject!,
      calendarName: flexCal.name,
      additionalAttendees: values.additionalAttendees!,
      additionalFieldResponses: parseAdditionalFieldResponse(
        values.additionalFieldResponses
      ),
    };
    //Index of selected location in event physical locations
    const physicalLocationIndex = eventPhysicalLocations.findIndex(
      (location) => location.placeId === selectedLocation.id
    );
    //Index of selected location in event virtual locations
    const virtualLocationIndex = eventVirtualLocations.findIndex(
      (location) => location.virtualLocationId === selectedLocation.id
    );
    //If selected location is physical set reservation location
    if (physicalLocationIndex !== -1) {
      reservation.physicalLocation =
        eventPhysicalLocations[physicalLocationIndex];
    }
    //If selected location is virtual set reservation location
    if (virtualLocationIndex !== -1) {
      reservation.virtualLocation = eventVirtualLocations[virtualLocationIndex];
    }

    try {
      //Send meeting reservation to BE
      await fetch(
        `/api/flexCals/reserveTimeSlotAsync/${flexCal.lazyCalendarId}?openingId=${clickedEvent.extendedProps.openingId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(reservation),
        }
      ).then(async (response) => {
        if (response.ok) {
          //Capture AcceptedInvitation response from BE call
          response.json().then((json) => {
            const reservationId: string = json;
            //Forward relevant data to acceptance page
            router.push({
              pathname: '/accept',
              query: {
                flexCalId: flexCal.lazyCalendarId,
                reservationId: reservationId,
                tzdbId: timeZone.tzdbId,
                tzbclId: timeZone.bclId,
                hostName: encodeURIComponent(apiUri),
                flexCalName: flexCal.name,
                userName: flexCal.displayName,
              },
            });
          });
        }
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `API call failed with status ${response.status}: ${errorBody}`
          );
        }
      });
      setSubmitting(false);
    } catch (err) {
      Sentry.captureException(err);
      setSubmitting(false);
      setLoading({
        isloading: false,
        loadingOrigin: LoadingOrigin.meetingForm,
      });
    }
  };

  const MeetingFormSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phone: Yup.string().required('Required'),
    subject: Yup.string().required('Required'),
    additionalFieldResponses: Yup.array().of(
      Yup.object().shape({
        response: Yup.string().test(
          'isRequired',
          'Required response',
          (value, context) => {
            const idx = parseInt(
              context.path.substring(
                context.path.indexOf('[') + 1,
                context.path.lastIndexOf(']')
              )
            );
            if (!additionalFields) return true;
            if (
              !value &&
              additionalFields[idx].additionalFieldStatus ===
                AdditionalFieldStatus.Required
            ) {
              return false;
            }
            return true;
          }
        ),
      })
    ),
  });

  if (eventLocations.length === 1) {
    handleLocationChange(eventLocations[0]);
  }

  return (
    <div className="flex flex-col px-3 py-2">
      <button
        className="flex flex-row items-center w-full gap-2 pt-2 lg:p-0 lg:w-1/4"
        onClick={() => setIsEventClicked(false)}
      >
        <BsChevronLeft size={15} />
        <h3 className="text-md">Go back</h3>
      </button>
      <h2 className="pt-4 text-lg font-bold lg:text-2xl">
        Confirm your meeting with {flexCal.displayName}
      </h2>
      <div className="flex flex-col gap-1 pt-2">
        <span className="flex flex-row gap-2">
          <h3 className="font-bold">Time: </h3>
          <h3>
            {clickedEvent?.extendedProps.interactive
              ? formatDate(
                  DateTime.fromJSDate(clickedEvent?.start).setZone(
                    timeZone.tzdbId
                  ),
                  timeZone
                )
              : // For regular Calendar - use the original logic
                formatDate(
                  DateTime.fromJSDate(clickedEvent?.start)
                    .setZone(timeZone.tzdbId)
                    .toUTC(),
                  timeZone
                )}
          </h3>
        </span>
        <span className="flex flex-row gap-2">
          <h3 className="font-bold">Meeting length: </h3>
          <h3>{eventDuration.toFormat('m')} minutes</h3>
        </span>
        <span className="flex flex-row items-center gap-2">
          <h3 className="font-bold">Location: </h3>
          <Listbox
            value={selectedLocation.name}
            onChange={handleLocationChange}
          >
            <div className="relative w-full lg:w-60">
              {selectedLocation.id === 0 && (
                <>
                  {' '}
                  <div className="absolute top-0 z-20 text-yellow-900 right-7 lg:top-1">
                    <IoWarningOutline size={25} />
                  </div>
                  <p className="absolute text-xs text-yellow-900 right-7 top-7 lg:top-9">
                    Required
                  </p>{' '}
                </>
              )}
              <Listbox.Button className="relative w-full py-1 pl-2 pr-8 text-sm text-left bg-white rounded-lg shadow-md cursor-default lg:w-60 lg:py-2 lg:pl-3 lg:pr-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 lg:text-md">
                <span
                  className={`block truncate ${
                    selectedLocation.id === 0
                      ? 'text-confirmed-secondary'
                      : 'text-black'
                  }`}
                >
                  {selectedLocation.name}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <HiSelector
                    className="w-5 h-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {eventLocations.map((location, index) => (
                    <Listbox.Option
                      key={index}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active
                            ? 'bg-confirmed-primary text-black'
                            : 'text-gray-900'
                        }`
                      }
                      value={location}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {location}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <HiCheck
                                className="w-5 h-5 text-confirmed-secondary"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </span>
      </div>
      <Formik
        enableReinitialize
        initialValues={formValues}
        validationSchema={MeetingFormSchema}
        validateOnMount
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          setLoading({
            isloading: true,
            loadingOrigin: LoadingOrigin.meetingForm,
          });
          await handleSubmit(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="flex flex-col justify-start mt-4 lg:flex-row">
            <div className="flex flex-col w-full lg:w-5/12">
              <h2 className="text-lg font-bold lg:text-xl">
                Your Information:{' '}
              </h2>
              <div className="flex flex-col w-full gap-5 pt-5">
                <div className="flex flex-row gap-3">
                  <div className="relative z-0 w-full">
                    <Field
                      as="input"
                      placeholder=" "
                      id="formField"
                      type="firstName"
                      name="firstName"
                      className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
                        errors.firstName && touched.firstName
                          ? 'border-yellow-900'
                          : 'border-confirmed-secondary'
                      }`}
                    />
                    <label
                      htmlFor="firstName"
                      className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
                      style={{ bottom: '6px' }}
                    >
                      First Name*
                    </label>
                    {errors.firstName && touched.firstName ? (
                      <>
                        <div className="absolute text-yellow-900 right-1 top-1">
                          <IoWarningOutline size={25} />
                        </div>
                        <p className="absolute text-xs text-yellow-900 right-1 top-9">
                          {errors.firstName}
                        </p>
                      </>
                    ) : null}
                  </div>
                  <div className="relative z-0 w-full">
                    <Field
                      as="input"
                      placeholder=" "
                      id="formField"
                      type="lastName"
                      name="lastName"
                      className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
                        errors.lastName && touched.lastName
                          ? 'border-yellow-900'
                          : 'border-confirmed-secondary'
                      }`}
                    />
                    <label
                      htmlFor="lastName"
                      className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
                      style={{ bottom: '6px' }}
                    >
                      Last Name*
                    </label>
                    {errors.lastName && touched.lastName ? (
                      <>
                        <div className="absolute text-yellow-900 right-1 top-1">
                          <IoWarningOutline size={25} />
                        </div>
                        <p className="absolute text-xs text-yellow-900 right-1 top-9">
                          {errors.lastName}
                        </p>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-row gap-3">
                  <div className="relative z-0 w-full">
                    <Field
                      as="input"
                      placeholder=" "
                      id="formField"
                      type="email"
                      name="email"
                      className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
                        errors.email && touched.email
                          ? 'border-yellow-900'
                          : 'border-confirmed-secondary'
                      }`}
                    />
                    <label
                      htmlFor="email"
                      className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
                      style={{ bottom: '6px' }}
                    >
                      Email*
                    </label>
                    {errors.email && touched.email ? (
                      <>
                        <div className="absolute text-yellow-900 right-1 top-1">
                          <IoWarningOutline size={25} />
                        </div>
                        <p className="absolute text-xs text-yellow-900 right-1 top-9">
                          {errors.email}
                        </p>
                      </>
                    ) : null}
                  </div>
                  <div className="relative z-0 w-full">
                    <Field
                      as="input"
                      placeholder=" "
                      id="formField"
                      type="phone"
                      name="phone"
                      className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
                        errors.phone && touched.phone
                          ? 'border-yellow-900'
                          : 'border-confirmed-secondary'
                      }`}
                    />
                    <label
                      htmlFor="phone"
                      className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
                      style={{ bottom: '6px' }}
                    >
                      Phone*
                    </label>
                    {errors.phone && touched.phone ? (
                      <>
                        <div className="absolute text-yellow-900 right-1 top-1">
                          <IoWarningOutline size={25} />
                        </div>
                        <p className="absolute text-xs text-yellow-900 right-1 top-9">
                          {errors.phone}
                        </p>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="relative z-0 w-full">
                  <Field
                    as="input"
                    placeholder=" "
                    id="formField"
                    type="subject"
                    name="subject"
                    className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
                      errors.subject && touched.subject
                        ? 'border-yellow-900'
                        : 'border-confirmed-secondary'
                    }`}
                  />
                  <label
                    htmlFor="subject"
                    className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
                    style={{ bottom: '6px' }}
                  >
                    Subject*
                  </label>
                  {errors.subject && touched.subject ? (
                    <>
                      <div className="absolute text-yellow-900 right-1 top-1">
                        <IoWarningOutline size={25} />
                      </div>
                      <p className="absolute text-xs text-yellow-900 right-1 top-9">
                        {errors.subject}
                      </p>
                    </>
                  ) : null}
                </div>
                {additionalFields?.map(
                  (af: FlexCalAdditionalField, index: number) => (
                    <div key={af.lazyCalendarAdditionalFieldId}>
                      {getFieldComponent(
                        af,
                        errors,
                        touched,
                        values,
                        index,
                        setFieldValue,
                        setFieldTouched
                      )}
                    </div>
                  )
                )}
              </div>
              <div className="absolute w-1/6 bottom-10 right-16">
                <div className="flex-row justify-end hidden w-full lg:flex">
                  <button
                    type="submit"
                    disabled={!(isValid && selectedLocation.id > 0)}
                    className={
                      'w-full py-1 rounded-md h-8 shadow-md bg-confirmed-secondary disabled:bg-blue-gray-100 disabled:hover:cursor-not-allowed'
                    }
                  >
                    {!loading?.isloading ? (
                      <p>Confirm</p>
                    ) : (
                      <ClipLoader
                        size={25}
                        color={'#86E3A8'}
                        className="m-auto"
                      />
                    )}
                  </button>
                </div>
                <p className="hidden text-sm lg:block">
                  Advanced technology to make your scheduling easier provided by{' '}
                  <br />
                  <a
                    href="https://confirmedapp.com"
                    className="text-confirmed-secondary max-h-5"
                    target="_blank"
                  >
                    <img
                      src="/favicon.ico"
                      alt="Confirmed Logo"
                      className="inline ml-1"
                      width={20}
                    />
                    ConfirmedApp.com
                  </a>
                </p>
              </div>
            </div>
            <div className="flex flex-col m-0 mt-3 lg:ml-32 lg:mr-auto lg:mt-0">
              <span className="flex flex-row mb-2 text-xl">
                <h2 className="mr-1 font-bold">Additional Attendees</h2>
                (optional):
              </span>
              <FieldArray name="additionalAttendees">
                {({ push, pop, remove }) => {
                  return (
                    <div className="flex flex-col gap-3">
                      {!addAttendee && (
                        <>
                          {values.additionalAttendees?.map((aa, index) => (
                            <div
                              key={index}
                              className="flex flex-row items-center"
                            >
                              <Tooltip
                                content={aa.email}
                                hidden={aa.name.length === 0}
                              >
                                <p className="font-bold">
                                  {aa.name.length === 0 ? aa.email : aa.name}
                                </p>
                              </Tooltip>
                              <button
                                type="button"
                                onClick={() => remove(index)}
                              >
                                <IoIosRemoveCircleOutline
                                  className="ml-1 text-red-500"
                                  size={26}
                                />
                              </button>
                            </div>
                          ))}
                          <button
                            className="flex flex-row items-center"
                            type="button"
                            onClick={() => {
                              setAddAttendee(true);
                              push({ name: '', email: '' });
                            }}
                          >
                            <p>Add More Attendees</p>
                            <AiOutlinePlusCircle
                              className="ml-1 text-confirmed-secondary"
                              size={25}
                            />
                          </button>
                        </>
                      )}
                      {addAttendee && (
                        <>
                          <Autocomplete
                            as="input"
                            id="formField"
                            type="attendeeEmail"
                            name={`additionalAttendees.${
                              values.additionalAttendees
                                ? values.additionalAttendees?.length - 1
                                : 0
                            }.email`}
                            getItemValue={(item: ExternalContact) => item.email}
                            items={contacts ? contacts : []}
                            renderItem={(
                              item: ExternalContact,
                              isHighlighted: boolean
                            ) => (
                              <div
                                style={{
                                  background: isHighlighted
                                    ? 'lightgray'
                                    : 'white',
                                }}
                              >
                                {item.email}
                              </div>
                            )}
                            renderMenu={(items: any[], value: string) => {
                              return (
                                <div className="absolute w-full z-20 flex flex-col p-3 empty:p-0 max-h-40 shadow-xl overflow-y-auto overflow-x-hidden rounded-md gap-2 bg-white text-[#607D8B]">
                                  {items.filter((item: JSX.Element) =>
                                    item.props.children
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toLowerCase())
                                  )}
                                </div>
                              );
                            }}
                            value={
                              values.additionalAttendees &&
                              values.additionalAttendees[
                                values.additionalAttendees.length - 1
                              ].email
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              setFieldValue(
                                `additionalAttendees.${
                                  values.additionalAttendees
                                    ? values.additionalAttendees?.length - 1
                                    : 0
                                }.email`,
                                e.target.value
                              )
                            }
                            onSelect={(val: string) =>
                              onSelectAttendee(
                                values,
                                setFieldValue,
                                val,
                                contacts,
                                AttendeeSelectType.Email
                              )
                            }
                            inputProps={{
                              placeholder: ' Email',
                              className:
                                'w-full py-1 pl-1 mt-1 z-0 bg-white border-0 border-b-2 rounded-md shadow-md appearance-none border-confirmed-secondary focus:outline-none focus:ring-0 focus:border-confirmed-primary',
                            }}
                            wrapperProps={{
                              className: 'w-full relative flex flex-col',
                            }}
                          />
                          <Autocomplete
                            type="attendeeName"
                            name={`additionalAttendees.${
                              values.additionalAttendees
                                ? values.additionalAttendees?.length - 1
                                : 0
                            }.name`}
                            getItemValue={(item: ExternalContact) => {
                              if (item.lastName === null) return item.firstName;
                              return item.firstName + ' ' + item.lastName;
                            }}
                            items={
                              contacts
                                ? contacts?.filter(
                                    (contact) => contact.firstName !== null
                                  )
                                : []
                            }
                            renderItem={(
                              item: ExternalContact,
                              isHighlighted: boolean
                            ) => (
                              <div
                                style={{
                                  background: isHighlighted
                                    ? 'lightgray'
                                    : 'white',
                                }}
                              >
                                {item.lastName !== null
                                  ? item.firstName + ' ' + item.lastName
                                  : item.firstName}
                              </div>
                            )}
                            renderMenu={(items: any[], value: string) => {
                              return (
                                <div className="absolute w-full z-20 flex flex-col p-3 empty:p-0 max-h-40 shadow-xl overflow-y-auto overflow-x-hidden rounded-md gap-2 bg-white text-[#607D8B]">
                                  {items.filter((item: JSX.Element) =>
                                    item.props.children
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toLowerCase())
                                  )}
                                </div>
                              );
                            }}
                            value={
                              values.additionalAttendees &&
                              values.additionalAttendees[
                                values.additionalAttendees.length - 1
                              ].name
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              setFieldValue(
                                `additionalAttendees.${
                                  values.additionalAttendees
                                    ? values.additionalAttendees?.length - 1
                                    : 0
                                }.name`,
                                e.target.value
                              )
                            }
                            onSelect={(val: string) =>
                              onSelectAttendee(
                                values,
                                setFieldValue,
                                val,
                                contacts,
                                AttendeeSelectType.Name
                              )
                            }
                            inputProps={{
                              placeholder: ' Name',
                              className:
                                'w-full py-1 pl-1 mt-1 z-0 bg-white border-0 border-b-2 rounded-md shadow-md appearance-none border-confirmed-secondary focus:outline-none focus:ring-0 focus:border-confirmed-primary',
                            }}
                            wrapperProps={{
                              className: 'w-full relative flex flex-col',
                            }}
                          />
                          <div className="flex flex-row justify-start w-full gap-4">
                            <button
                              type="button"
                              className="flex flex-row items-center justify-center px-3 py-1 rounded-md shadow-md w-min bg-confirmed-secondary"
                              onClick={() => setAddAttendee(false)}
                            >
                              <AiOutlinePlus />
                              <p>Add</p>
                            </button>
                            <button
                              type="button"
                              className="px-3 py-1 bg-white rounded-md shadow-md w-min"
                              onClick={() => {
                                pop();
                                setAddAttendee(false);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                }}
              </FieldArray>
            </div>
            <div className="flex flex-row justify-end py-6 lg:hidden">
              <button
                type="submit"
                disabled={!(isValid && selectedLocation.id > 0)}
                className={
                  'w-5/12 py-1 rounded-md h-8 shadow-md bg-confirmed-secondary disabled:bg-blue-gray-100 disabled:hover:cursor-not-allowed'
                }
              >
                {!loading?.isloading ? (
                  <p>Confirm</p>
                ) : (
                  <ClipLoader size={25} color={'#86E3A8'} className="m-auto" />
                )}
              </button>
            </div>
            <p className="px-12 py-4 text-sm lg:hidden">
              Advanced technology to make your scheduling easier provided by{' '}
              <a
                href="https://confirmedapp.com"
                className="text-confirmed-secondary max-h-5"
                target="_blank"
              >
                {' '}
                <img
                  src="/favicon.ico"
                  alt="Confirmed Logo"
                  className="inline ml-1"
                  width={20}
                />
                ConfirmedApp.com
              </a>
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MeetingForm;

const fieldErrorMessage = (
  errors: FormikErrors<Values>,
  touched: FormikTouched<Values>,
  index: number
) => {
  return (
    errors.additionalFieldResponses &&
    errors.additionalFieldResponses[index] &&
    (touched as any).additionalFieldResponses &&
    (touched as any).additionalFieldResponses[index] && (
      <>
        <div className="absolute text-yellow-900 right-1 top-1">
          <IoWarningOutline size={25} />
        </div>
        <p className="absolute text-xs text-yellow-900 right-1 top-9">
          {overrideYupTypeError(
            errors.additionalFieldResponses[index],
            'response'
          )}
        </p>
      </>
    )
  );
};

const handleRadioChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  index: number,
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean | undefined
  ) => void,
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void
) => {
  setFieldTouched(`additionalFieldResponses.${index}.response`, true, true);
  setFieldValue(
    `additionalFieldResponses.${index}.response`,
    e.target.value,
    true
  );
};

const handleCheckboxChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  index: number,
  values: Values,
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean | undefined
  ) => void,
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void
) => {
  if (!values.additionalFieldResponses) {
    return;
  }
  let response = values.additionalFieldResponses[index].response;
  if (response.includes(e.target.value)) {
    if (response.includes('|')) {
      if (response.includes('|' + e.target.value)) {
        response = response.replace('|' + e.target.value, '');
      } else {
        response = response.replace(e.target.value + '|', '');
      }
    } else {
      response = response.replace(e.target.value, '');
    }
  } else {
    response
      ? (response += '|' + e.target.value)
      : (response += e.target.value);
  }
  setFieldTouched(`additionalFieldResponses.${index}.response`, true, true);
  setFieldValue(`additionalFieldResponses.${index}.response`, response, true);
};

const getFieldComponent = (
  additionalField: FlexCalAdditionalField,
  errors: FormikErrors<Values>,
  touched: FormikTouched<Values>,
  values: Values,
  index: number,
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean | undefined
  ) => void,
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void
) => {
  switch (additionalField.additionalFieldType) {
    case AdditionalFieldType.Text:
      return field(errors, touched, additionalField, index);
    case AdditionalFieldType.LargeText:
      return (
        <div className="relative z-0 w-full">
          <Field
            as="textarea"
            placeholder=" "
            id="formField"
            name={`additionalFieldResponses.${index}.response`}
            rows={3}
            className={`block resize-none w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
              errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              typeof (touched as any).additionalFieldResponses === 'object' &&
              ((touched as any).additionalFieldResponses as any)[index]
                ? 'border-yellow-900'
                : 'border-confirmed-secondary'
            }`}
          />
          <label
            htmlFor="subject"
            className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
            style={{ top: '4px' }}
          >
            {additionalField.fieldName}
            {additionalField.additionalFieldStatus ===
              AdditionalFieldStatus.Required && '*'}
          </label>
          <> {fieldErrorMessage(errors, touched, index)}</>
        </div>
      );
    case AdditionalFieldType.Checkbox:
      return (
        <div className="relative z-0 w-full">
          <h3
            className={`${
              errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              typeof (touched as any).additionalFieldResponses === 'object' &&
              ((touched as any).additionalFieldResponses as any)[index]
                ? 'text-yellow-900'
                : 'text-confirmed-secondary'
            }`}
          >
            {additionalField.fieldName}
            {additionalField.additionalFieldStatus ===
              AdditionalFieldStatus.Required && '*'}
            {errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              (touched as any).additionalFieldResponses[index] &&
              ' (Required)'}
          </h3>
          <div
            role="group"
            aria-labelledby="checkbox-group"
            className="flex flex-col"
          >
            {additionalField.lazyCalendarAdditionalFieldOptions.map(
              (option, idx) => {
                return (
                  <Checkbox
                    color="teal"
                    label={option.option}
                    key={idx}
                    id={index + '.' + idx.toString()}
                    value={option.option}
                    name={`additionalFieldResponses.${index}.response`}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        index,
                        values,
                        setFieldValue,
                        setFieldTouched
                      )
                    }
                  />
                );
              }
            )}
          </div>
        </div>
      );
    case AdditionalFieldType.Radio:
      return (
        <div className="relative z-0 w-full">
          <h3
            className={`${
              errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              (touched as any).additionalFieldResponses[index]
                ? 'text-yellow-900'
                : 'text-confirmed-secondary'
            }`}
          >
            {additionalField.fieldName}
            {additionalField.additionalFieldStatus ===
              AdditionalFieldStatus.Required && '*'}
            {errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              (touched as any).additionalFieldResponses[index] &&
              ' (Required)'}
          </h3>
          <div role="group" className="flex flex-col">
            {additionalField.lazyCalendarAdditionalFieldOptions.map(
              (option, idx) => {
                return (
                  <Radio
                    color="teal"
                    key={idx}
                    id={index + '.' + idx.toString()}
                    value={option.option}
                    label={option.option}
                    name={`additionalFieldResponses.${index}.response`}
                    onChange={(e) =>
                      handleRadioChange(
                        e,
                        index,
                        setFieldValue,
                        setFieldTouched
                      )
                    }
                  />
                );
              }
            )}
          </div>
        </div>
      );
    case AdditionalFieldType.Select:
      return (
        <div className="relative w-full z-5">
          <h3
            className={`${
              errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              (touched as any).additionalFieldResponses[index]
                ? 'text-yellow-900'
                : 'text-confirmed-secondary'
            }`}
          >
            {additionalField.fieldName}
            {additionalField.additionalFieldStatus ===
              AdditionalFieldStatus.Required && '*'}
            {errors.additionalFieldResponses &&
              errors.additionalFieldResponses[index] &&
              (touched as any).additionalFieldResponses &&
              (touched as any).additionalFieldResponses[index] &&
              ' (Required)'}
          </h3>
          <Select
            color="teal"
            className="z-10"
            onClick={() =>
              setFieldTouched(
                `additionalFieldResponses.${index}.response`,
                true,
                true
              )
            }
            onChange={(e) => {
              if (!e) return;
              setFieldValue(
                `additionalFieldResponses.${index}.response`,
                e.toString()
              );
            }}
          >
            {additionalField.lazyCalendarAdditionalFieldOptions.map(
              (option, idx) => {
                return (
                  <Option value={option.option} color="teal" key={idx}>
                    {option.option}
                  </Option>
                );
              }
            )}
          </Select>
        </div>
      );
    case AdditionalFieldType.Phone:
      return field(errors, touched, additionalField, index);
    case AdditionalFieldType.Address:
      return field(errors, touched, additionalField, index);
    default:
      return field(errors, touched, additionalField, index);
  }
};

const field = (
  errors: FormikErrors<Values>,
  touched: FormikTouched<Values>,
  additionalField: FlexCalAdditionalField,
  index: number
) => {
  return (
    <div className="relative z-0 w-full">
      <Field
        as="input"
        placeholder=" "
        id="formField"
        name={`additionalFieldResponses.${index}.response`}
        className={`block w-full py-1 pl-1 mt-0 bg-transparent bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-md appearance-none focus:outline-none focus:ring-0 focus:border-confirmed-primary ${
          errors.additionalFieldResponses &&
          errors.additionalFieldResponses[index] &&
          (touched as any).additionalFieldResponses &&
          (touched as any).additionalFieldResponses[index]
            ? 'border-yellow-900'
            : 'border-confirmed-secondary'
        }`}
      />
      <label
        htmlFor="subject"
        className="absolute pl-1 text-gray-500 duration-300 pointer-events-none -z-1 origin-0"
        style={{ bottom: '6px' }}
      >
        {additionalField.fieldName}
        {additionalField.additionalFieldStatus ===
          AdditionalFieldStatus.Required && '*'}
      </label>
      <>{fieldErrorMessage(errors, touched, index)}</>
    </div>
  );
};

const parseAdditionalFieldResponse = (
  responses: InvitationAdditionalFieldResponse[] | undefined
) => {
  if (!responses) return [];

  const checkboxResponses: InvitationAdditionalFieldResponse[] = [];
  const newResponses: InvitationAdditionalFieldResponse[] = [];

  responses.forEach((af) => {
    if (af.response.includes('|')) {
      const checkBoxValues = af.response.split('|');
      checkBoxValues.forEach((cbv) =>
        checkboxResponses.push({
          fieldName: af.fieldName,
          response: cbv,
          additionalFieldType: af.additionalFieldType,
        })
      );
    } else if (af.response && af.response.trim() !== '') {
      newResponses.push(af);
    }
  });

  return [...newResponses, ...checkboxResponses];
};

const onSelectAttendee = (
  values: Values,
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void,
  input: string,
  contacts: ExternalContact[] | undefined,
  inputType: AttendeeSelectType
) => {
  if (!contacts) return;
  if (inputType === 1) {
    setFieldValue(
      `additionalAttendees.${
        values.additionalAttendees ? values.additionalAttendees?.length - 1 : 0
      }.email`,
      input
    );
    const contactIdx = contacts.findIndex((contact) => contact.email === input);
    if (contactIdx !== -1) {
      let contactName = '';
      if (
        contacts[contactIdx] === null ||
        contacts[contactIdx].firstName === null
      )
        return;
      if (contacts[contactIdx].lastName === null)
        contactName = contacts[contactIdx].firstName;
      else
        contactName =
          contacts[contactIdx].firstName + ' ' + contacts[contactIdx].lastName;
      setFieldValue(
        `additionalAttendees.${
          values.additionalAttendees
            ? values.additionalAttendees?.length - 1
            : 0
        }.name`,
        contactName
      );
    }
  } else {
    setFieldValue(
      `additionalAttendees.${
        values.additionalAttendees ? values.additionalAttendees?.length - 1 : 0
      }.name`,
      input
    );
    const contactIdx = contacts.findIndex(
      (contact) => contact.firstName + ' ' + contact.lastName === input
    );
    if (contactIdx !== -1) {
      const contactEmail = contacts[contactIdx].email;
      setFieldValue(
        `additionalAttendees.${
          values.additionalAttendees
            ? values.additionalAttendees?.length - 1
            : 0
        }.email`,
        contactEmail
      );
    }
  }
};
